import { Check, NotebookPen, Plus, Trash2, User } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useModalStore } from "store/modal/hooks/useModalStore";
import { getCompanyDetails, ScrollToElement } from "utils";

import { Button } from "@krea/shared/design-system/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@krea/shared/design-system/Popover";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { cn } from "@krea/shared/utils";

import { AssignLenderUserSelect } from "@/components/assign-lender-user-select";
import RejectApplicationModal from "@/components/modals/reject-application-modal";
import { LenderApplicationTypeEnum } from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";

import { MarkPaidOutModal } from "../../modals/mark-paid-out-modal";

const ContactItem = ({
  title,
  value,
  href,
  isEmail,
}: {
  title: string;
  value?: string;
  href?: string;
  isEmail?: boolean;
}) => {
  const inner =
    href || isEmail ? (
      <a
        href={isEmail ? `mailto:${value}` : href}
        className={cn({ inline: isEmail })}
      >
        {value}
      </a>
    ) : (
      <span>{value}</span>
    );

  return (
    <dl className="my-2 flex flex-col overflow-hidden p-0">
      <dt
        className="mb-1 w-full font-semibold text-gray-100"
        style={{ fontSize: "14px" }}
      >
        {title}
      </dt>

      <dd className="m-0 block w-full truncate p-0">
        {isEmail && value ? (
          <ResponsiveTooltip trigger={inner}>{value}</ResponsiveTooltip>
        ) : (
          inner
        )}
      </dd>
    </dl>
  );
};

interface IToolbarProps {
  lenderApplication: ILenderApplicationExtended;
}

const Toolbar = ({ lenderApplication }: IToolbarProps) => {
  const { lenderCompanyId } = useParams();
  const { loanApplication, assignedLenderUser } = lenderApplication;
  const {
    applicationRejectedByLenderAfterWinningAt: rejectedAfterWinningAt,
    type,
  } = lenderApplication;

  const { applyingCompany, selectedLenderResponse, assignedAdminUser } =
    loanApplication;
  const { customerCompany } = applyingCompany;

  const showRejectAfterWinningModal = useModalStore(
    (state) => state.showRejectAfterWinningModal,
  );
  const showReopenModal = useModalStore((state) => state.showReopenModal);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showMarkAsPaidOutModal, setShowMarkAsPaidOutModal] = useState(false);
  const countryCode = lenderApplication.loanApplication.countryCode;

  const contacts = getCompanyDetails(countryCode);

  const onShowRejectModal = () => {
    if (type === LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED)
      showRejectAfterWinningModal({
        lenderApplicationId: lenderApplication.id,
        lenderCompanyId: Number(lenderCompanyId),
      });
    else {
      setShowRejectModal(true);
    }
  };

  const onReopenApplication = () => {
    showReopenModal({
      lenderApplicationId: lenderApplication.id,
      lenderCompanyId: Number(lenderCompanyId),
    });
  };

  const onMarkAsPaidOut = () => {
    setShowMarkAsPaidOutModal(true);
  };

  return (
    <div className="mb-6">
      <div className="-mx-3 flex flex-wrap justify-end">
        <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
          <AssignLenderUserSelect
            lenderApplicationId={lenderApplication.id}
            assignedLenderUser={assignedLenderUser}
            className="w-full max-w-60"
          />
        </div>
        {type ===
          LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED && (
          <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
            <Button
              id="mark-as-paid-out"
              variant="success"
              className="w-full px-4"
              onClick={() => onMarkAsPaidOut()}
            >
              <Check className="size-4" />
              Mark as paid out
            </Button>
          </div>
        )}

        {type !== LenderApplicationTypeEnum.REJECTED &&
          type !==
            LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER &&
          type !==
            LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED &&
          type !==
            LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER &&
          type !== LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER &&
          type !== LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER && (
            <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
              <Button
                variant="default"
                className="w-full px-4"
                onClick={() => ScrollToElement("scrollToBidsForm")}
              >
                <Plus className="size-4" />
                Place an offer
              </Button>
            </div>
          )}

        {type !== LenderApplicationTypeEnum.REJECTED &&
          type !==
            LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER &&
          type !== LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER &&
          type !==
            LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER &&
          type !== LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER && (
            <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
              <Button
                variant="danger"
                className="w-full px-4"
                onClick={() => onShowRejectModal()}
              >
                <Trash2 className=" size-4" />
                Reject
              </Button>
            </div>
          )}

        {type === LenderApplicationTypeEnum.REJECTED &&
        !rejectedAfterWinningAt ? (
          <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
            <Button
              variant="secondary"
              className="w-full px-4"
              onClick={() => onReopenApplication()}
            >
              Reopen
            </Button>
          </div>
        ) : null}

        <div className="mb-4 w-full shrink-0 px-3 md:w-auto">
          <Button
            variant="secondary"
            className="w-full px-4"
            onClick={() => ScrollToElement("scrollToNotes")}
          >
            <NotebookPen className="size-4" />
            Notes
          </Button>
        </div>

        {assignedAdminUser && (
          <div className="w-full shrink-0 px-3 md:w-auto">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" className="w-full">
                  <User className="size-4" />
                  {`${contacts.companyName} contact: ${assignedAdminUser.firstName}`}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                side="bottom"
                sideOffset={5}
                className="min-w-[220px] max-w-[290px]"
              >
                <div className="px-4 pt-2">
                  {(assignedAdminUser.firstName ||
                    assignedAdminUser.lastName) && (
                    <div className="mb-4 h-6">{`${assignedAdminUser.firstName} ${assignedAdminUser.lastName}`}</div>
                  )}

                  <ContactItem
                    isEmail
                    title="Email"
                    value={
                      assignedAdminUser.email
                        ? assignedAdminUser.email
                        : contacts.email
                    }
                  />

                  <ContactItem
                    title="Phone"
                    value={
                      assignedAdminUser.mobilePhoneNumber
                        ? assignedAdminUser.mobilePhoneNumber
                        : contacts.phone?.number
                    }
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
      {showRejectModal && (
        <RejectApplicationModal
          setOpen={(isOpen: boolean) => setShowRejectModal(isOpen)}
          customerCompany={customerCompany}
          lenderApplicationId={lenderApplication.id}
        />
      )}
      {showMarkAsPaidOutModal && selectedLenderResponse && (
        <MarkPaidOutModal
          setOpen={(isOpen: boolean) => setShowMarkAsPaidOutModal(isOpen)}
          lenderResponse={selectedLenderResponse}
          lenderApplication={lenderApplication}
        />
      )}
    </div>
  );
};

export default Toolbar;
